import {
  Autocomplete,
  Button,
  Drawer,
  Grid,
  IconButton,
  Paper,
  TextField,
} from "@mui/material";
import DrawerSkeleton from "../helpers/components/DrawerSkeleton";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Box, Stack } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { UniqueDefinitionsEndpointLogic } from "../../../../../logic/DataCollection/Unifier/UniqueDefinitionsEndpointLogic";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import DuplicateDefinition from "./DuplicateDefinition";
import {
  setSelectedDestinationTableDuplicateScreen,
  setSelectedInterfaceDuplicateScreen,
  setUniqueDefinitionData,
} from "../../../../../features/uniqueDefSlice";
type ForceRefreshResult = {
  filteringPayload: string | null;
  lookupPayload: string | null;
};
const DuplicateDefinitionSidebar = ({ open, onClose, loading }: any) => {
  const {
    initialDuplicateData,
    interfaceData,
    destinationTablesData,
    lookupFieldDataForPayload,
    rowFilteringDataForPayload,
    isValidRowFiltering,
    isPreviewEmpty,
    selectedInterfaceDuplicateScreen,
    selectedDestinationTableDuplicateScreen,
    isLookupFieldsValid,
  } = useSelector((state: any) => state.uniqueDefinition);
  const dispatch = useDispatch();
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [forceRefresh, setForceRefresh] = useState<
    null | (() => Promise<ForceRefreshResult>)
  >(null);
  const oauthTokenHeader = useSelector(selectUserToken) as any;

  useEffect(() => {
    if (!initialDuplicateData || !interfaceData || !destinationTablesData)
      return;

    const interfaceObj = interfaceData.find(
      (i: any) =>
        i.affiliate_system_interface_name ===
        initialDuplicateData.affiliate_system_interface,
    );

    const destinationObj = destinationTablesData.find(
      (d: any) =>
        d.destination_table === initialDuplicateData.destination_table_name,
    );

    if (interfaceObj) {
      dispatch(setSelectedInterfaceDuplicateScreen(interfaceObj));
    }

    if (destinationObj) {
      dispatch(setSelectedDestinationTableDuplicateScreen(destinationObj));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDuplicateData, interfaceData, destinationTablesData]);

  const handleInterfaceChange = (_: any, value: any, reason: string) => {
    const selectedTable = value
      ? interfaceData?.find((item: any) => item.id === value.id)
      : null;

    dispatch(setSelectedInterfaceDuplicateScreen(selectedTable));

    if (reason === "clear") {
      dispatch(setUniqueDefinitionData([]));
    }
  };

  const handleDestinationTableChange = (_: any, value: any, reason: string) => {
    const selectedTable = value
      ? destinationTablesData?.find((item: any) => item.id === value.id)
      : null;

    dispatch(setSelectedDestinationTableDuplicateScreen(selectedTable));

    // Update URL parameters

    if (reason === "clear") {
      dispatch(setUniqueDefinitionData([]));
    }
  };

  // Find IDs by matching names with the data
  const intID = selectedInterfaceDuplicateScreen?.id;
  const destTableID = selectedDestinationTableDuplicateScreen?.id;

  const handleDuplicateUniqueDefinition = async () => {
    if (!forceRefresh) return;
    const { filteringPayload, lookupPayload } = await forceRefresh();
    // Validation
    if (!intID) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Please select an interface.",
          type: "error",
        }),
      );
      return;
    }

    if (!destTableID) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Please select a destination table.",
          type: "error",
        }),
      );
      return;
    }

    if (!rowFilteringDataForPayload) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Filtering definition is required.",
          type: "error",
        }),
      );
      return;
    }

    if (!lookupFieldDataForPayload) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Unique definition is required.",
          type: "error",
        }),
      );
      return;
    }

    setIsDuplicating(true);

    try {
      const payload = {
        filtering_definition: filteringPayload,
        unique_definition: lookupPayload,
        backend_interface_id: Number(intID),
        destination_table_id: Number(destTableID),
      };

      const response =
        await UniqueDefinitionsEndpointLogic.createUnifierUniqueDefinition(
          oauthTokenHeader,
          payload,
        );

      if (response?.status === 201) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Unique definition created successfully!",
            type: "success",
          }),
        );
        onClose();
        await UniqueDefinitionsEndpointLogic.getAllUnifierUniqueDefinitions(
          oauthTokenHeader,
        );
      } else {
        throw new Error(
          response?.reason || "Failed to create unique definition",
        );
      }
    } catch (error: any) {
      console.error("Error creating unique definition:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error?.reason ||
            error?.message ||
            "Failed to create unique definition",
          type: "error",
        }),
      );
    } finally {
      setIsDuplicating(false);
    }
  };
  return (
    <Drawer
      anchor="right"
      sx={{ zIndex: 1300 }}
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      PaperProps={{
        sx: {
          width: "50%",
        },
      }}
    >
      {loading ? (
        <DrawerSkeleton />
      ) : (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              pb: 2,
              px: 2,
            }}
          >
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>

            <Grid container spacing={2} sx={{ mt: 1, pl: 1 }}>
              <Grid item xs={12} sm={5}>
                <Stack spacing={1}>
                  <Autocomplete
                    value={
                      selectedInterfaceDuplicateScreen
                        ? {
                            label:
                              selectedInterfaceDuplicateScreen.affiliate_system_interface_name,
                            id: selectedInterfaceDuplicateScreen.id,
                          }
                        : null
                    }
                    options={interfaceData.map((item: any) => ({
                      label: item.affiliate_system_interface_name,
                      id: item.id,
                    }))}
                    onChange={handleInterfaceChange}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        data-tracker="381"
                        {...params}
                        placeholder="Search interface or choose from the list"
                        label="Interface"
                        required
                        InputProps={{
                          ...params.InputProps,
                        }}
                        color="secondary"
                        size="small"
                        variant="standard"
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Stack spacing={1}>
                  <Autocomplete
                    value={
                      selectedDestinationTableDuplicateScreen
                        ? {
                            label:
                              selectedDestinationTableDuplicateScreen.destination_table,
                            id: selectedDestinationTableDuplicateScreen.id,
                          }
                        : null
                    }
                    options={destinationTablesData.map((item: any) => ({
                      label: item.destination_table,
                      id: item.id,
                    }))}
                    onChange={handleDestinationTableChange}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        data-tracker="382"
                        {...params}
                        placeholder="Search table or choose from the list"
                        label="Destination Table"
                        required
                        InputProps={{
                          ...params.InputProps,
                        }}
                        color="secondary"
                        size="small"
                        variant="standard"
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid
                item
                xs={12}
                sm={2}
                sx={{
                  justifyContent: "flex-end",
                  alignItems: "flex-start",
                  display: "flex",
                }}
              >
                <Button
                  data-tracker="390"
                  variant="contained"
                  color="secondary"
                  sx={{ whiteSpace: "nowrap" }}
                  onClick={handleDuplicateUniqueDefinition}
                  disabled={
                    isDuplicating ||
                    !rowFilteringDataForPayload ||
                    !isLookupFieldsValid ||
                    isPreviewEmpty ||
                    !intID ||
                    !destTableID ||
                    !isValidRowFiltering
                  }
                >
                  <SaveOutlinedIcon sx={{ width: "25px", mr: "5px" }} />
                  Save
                  {isDuplicating && <SimpleBackdrop />}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <DuplicateDefinition onRegisterForceRefresh={setForceRefresh} />
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default DuplicateDefinitionSidebar;
